import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Modal } from 'atoms';
import { PopupContainer } from 'molecules';
import { usePaymentMutations } from 'core/hooks';

import * as L from '../StudentDetail.styled';
import { IAgreement, IContribution, IPayment } from './types';

type PopupMode =
  | {
      kind: 'pay';
      contribution: IContribution;
    }
  | {
      kind: 'edit';
      editing: IPayment & { agreementId: string };
    };

interface IAddPaymentInlinePopupProps {
  /** Used to render the agreement chip in the form header; in both
   *  modes the agreement is locked. */
  agreements: IAgreement[];
  defaultAgreementId?: string;
  /** Edit-existing-payment mode. */
  editing?: IPayment & { agreementId: string };
  /** Pay-a-scheduled-contribution mode. */
  contribution?: IContribution;
  studentId: string;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Inline modal used in two well-defined modes:
 *
 *   - **pay**: `contribution` is set, `editing` is absent. The form is
 *     prefilled from the contribution (date locked, amount defaults to
 *     `remainder ?? amountForeign` and is editable so the financier can
 *     pay less for a partial close). `createPayment` is called with
 *     `contributionId` — backend atomically wires Payment ↔ Contribution
 *     via §9.4.
 *
 *   - **edit**: `editing` is set, `contribution` is absent. Date is
 *     editable, amount is locked, the agreement is fixed.
 *
 * The previous «free orphan create» mode is gone: in the unified
 * schedule-first model every Payment must close a Contribution, so the
 * only entry points for creating a new Payment go through `contribution`
 * (here) or `PickInvoiceForContributionPopup`. Any code that tries to
 * open this popup without one of the two anchors is a bug.
 */
export const AddPaymentInlinePopup = memo(
  ({
    agreements,
    defaultAgreementId,
    editing,
    contribution,
    studentId,
    onClose,
    onSaved,
  }: IAddPaymentInlinePopupProps) => {
    const { t } = useTranslation();
    const { createPayment, updatePayment, loading } = usePaymentMutations(
      studentId,
    );

    const mode = useMemo<PopupMode>(() => {
      if (editing) return { kind: 'edit', editing };
      if (contribution) return { kind: 'pay', contribution };
      // Defensive: surface a clear error rather than silently no-op.
      // The popup is only mounted from PaymentsBlock which always
      // supplies one of the two anchors — getting here is a wiring bug.
      throw new Error(
        'AddPaymentInlinePopup requires either `editing` or `contribution`.',
      );
    }, [editing, contribution]);

    const initialAgreementId =
      mode.kind === 'edit'
        ? mode.editing.agreementId
        : defaultAgreementId || agreements[0]?.id || '';

    const remainderToPay = useMemo(() => {
      if (mode.kind !== 'pay') return null;
      const r = mode.contribution.remainder ?? mode.contribution.amountForeign;
      return r ?? null;
    }, [mode]);

    const [agreementId] = useState(initialAgreementId);
    const [amount, setAmount] = useState<string>(
      mode.kind === 'edit'
        ? String(mode.editing.amountAgreement)
        : remainderToPay !== null
        ? String(remainderToPay)
        : '',
    );
    const [date, setDate] = useState<string>(
      mode.kind === 'edit'
        ? moment(mode.editing.date).format('YYYY-MM-DD')
        : moment(mode.contribution.date).format('YYYY-MM-DD'),
    );

    const selectedAgreement = agreements.find((a) => a.id === agreementId);
    const currency =
      mode.kind === 'edit'
        ? mode.editing.currency || selectedAgreement?.currency || ''
        : selectedAgreement?.currency || '';
    const lockAmount = mode.kind === 'edit';

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        const num = parseFloat(amount.replace(',', '.'));
        if (!num || num <= 0 || !agreementId || !date) return;

        let ok = false;
        if (mode.kind === 'edit') {
          ok = await updatePayment({
            paymentId: mode.editing.id,
            amountAgreement: num,
            date,
          });
        } else {
          ok = await createPayment({
            agreementId,
            amountAgreement: num,
            date,
            currency,
            contributionId: mode.contribution.id,
          });
        }
        if (ok) {
          onSaved?.();
          onClose();
        }
      },
      [
        amount,
        agreementId,
        date,
        mode,
        updatePayment,
        createPayment,
        currency,
        onSaved,
        onClose,
      ],
    );

    return (
      <Modal>
        <PopupContainer
          width="480px"
          title={
            mode.kind === 'edit'
              ? t('pages.studentDetail.payments.editTitle')
              : t('pages.studentDetail.payments.payContributionTitle')
          }
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              {mode.kind === 'pay' ? (
                <L.InlineNotice
                  style={{
                    background: '#f0f9ff',
                    borderColor: '#bae6fd',
                    color: '#075985',
                  }}
                >
                  {t('pages.studentDetail.payments.payContributionHint', {
                    date: moment(mode.contribution.date).format('DD.MM.YYYY'),
                    amount: mode.contribution.amountForeign ?? '—',
                    currency: currency || '—',
                  })}
                </L.InlineNotice>
              ) : null}

              <L.FormLabel>
                {t('pages.studentDetail.payments.form.agreement')}
                <L.FormSelect value={agreementId} disabled>
                  {agreements.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.agreementTitle} · {a.currency}
                      {a.group?.name ? ` · ${a.group.name}` : ''}
                    </option>
                  ))}
                </L.FormSelect>
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.studentDetail.payments.form.amount', {
                  currency: currency || '—',
                })}
                <L.FormInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  disabled={lockAmount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  autoFocus
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.studentDetail.payments.form.date')}
                <L.FormInput
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </L.FormLabel>

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                >
                  {t('pages.studentDetail.payments.form.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={loading || !amount || !agreementId || !date}
                >
                  {loading
                    ? t('pages.studentDetail.payments.form.saving')
                    : mode.kind === 'edit'
                    ? t('pages.studentDetail.payments.form.save')
                    : t('pages.studentDetail.payments.form.create')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
